/** Iter 113 — editable Price Details + live Booking Price Summary for manual
 * booking create/edit. All fields optional overrides; live math via
 * computeManualPricing. Shows "Manual override" badges when a calculated
 * value was changed. */
import { Input } from "@/components/ui/input";
import Money from "@/components/Money";
import { Plus, Trash2, Receipt, PenLine } from "lucide-react";
import { computeManualPricing } from "@/lib/manualPricing";

const METHODS = ["Cash", "Card", "Bank transfer", "POS", "Stripe", "Other"];

const OverrideBadge = () => (
  <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 py-px ml-1" data-testid="override-badge">
    <PenLine className="h-2.5 w-2.5" /> Override
  </span>
);

function Field({ label, calc, value, onChange, placeholder, overridden, testId }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-neutral-500 font-semibold flex items-center whitespace-nowrap overflow-hidden">
        {label}{overridden && <OverrideBadge />}
      </div>
      <Input type="number" inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || (calc != null ? `auto (€${calc.toFixed(2)})` : "auto")}
        className={`mt-0.5 h-8 rounded-lg text-sm ${overridden ? "border-amber-400 bg-amber-50/40" : ""}`} data-testid={testId} />
    </div>
  );
}

export function ManualBookingPriceDetails({ base, spec, setSpec, extras, insuranceName, showAmountPaid = true, paidSoFar = 0 }) {
  const p = (k) => (v) => setSpec((s) => ({ ...s, [k]: v }));
  const calc = computeManualPricing(base, spec, extras);

  const setCharge = (i, patch) => setSpec((s) => ({
    ...s, additional_charges: s.additional_charges.map((c, j) => (j === i ? { ...c, ...patch } : c)),
  }));

  return (
    <div className="space-y-3" data-testid="manual-price-details">
      <div className="rounded-xl border border-border p-3 space-y-2.5">
        <div className="text-xs uppercase tracking-wider text-neutral-500 font-semibold flex items-center gap-1.5">
          <Receipt className="h-3.5 w-3.5" /> Price details
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Price / day" calc={calc.days ? calc.rentalCalc / calc.days : 0}
            value={spec.daily_rate_override} onChange={p("daily_rate_override")} testId="price-daily-rate" />
          <Field label="Rental subtotal" calc={calc.rentalCalc} overridden={calc.overrides.rental}
            value={spec.rental_subtotal_override} onChange={p("rental_subtotal_override")} testId="price-rental-subtotal" />
          <Field label="Delivery / one-way fee" calc={calc.routeCalc} overridden={calc.overrides.route}
            value={spec.route_fee_override} onChange={p("route_fee_override")} testId="price-route-fee" />
          <Field label="Deposit" calc={calc.depCalc} overridden={calc.overrides.deposit}
            value={spec.deposit_override} onChange={p("deposit_override")} testId="price-deposit" />
        </div>

        {/* Discount + VAT */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-neutral-500 font-semibold">Discount</div>
            <div className="flex gap-1 mt-0.5">
              <select value={spec.discount_type} onChange={(e) => p("discount_type")(e.target.value)}
                className="rounded-lg border border-border px-1.5 text-xs h-8" data-testid="price-discount-type">
                <option value="fixed">€</option>
                <option value="percent">%</option>
              </select>
              <Input type="number" inputMode="decimal" value={spec.discount_value} onChange={(e) => p("discount_value")(e.target.value)}
                placeholder="0" className="h-8 rounded-lg text-sm" data-testid="price-discount-value" />
            </div>
          </div>
          <Field label="VAT / Tax %" value={spec.vat_rate_pct} onChange={p("vat_rate_pct")} placeholder="0" testId="price-vat" />
        </div>

        {/* Additional charges */}
        <div>
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-wide text-neutral-500 font-semibold">Additional charges</span>
            <button type="button" onClick={() => setSpec((s) => ({ ...s, additional_charges: [...s.additional_charges, { label: "", amount: "" }] }))}
              className="text-xs font-bold text-blue-600 inline-flex items-center gap-0.5" data-testid="price-add-charge">
              <Plus className="h-3 w-3" /> Add
            </button>
          </div>
          {spec.additional_charges.map((c, i) => (
            <div key={i} className="flex gap-1.5 mt-1" data-testid={`price-charge-row-${i}`}>
              <Input value={c.label} onChange={(e) => setCharge(i, { label: e.target.value })} placeholder="e.g. Airport delivery" className="h-8 rounded-lg text-sm flex-1" />
              <Input type="number" value={c.amount} onChange={(e) => setCharge(i, { amount: e.target.value })} placeholder="€" className="h-8 rounded-lg text-sm w-20" />
              <button type="button" onClick={() => setSpec((s) => ({ ...s, additional_charges: s.additional_charges.filter((_, j) => j !== i) }))} className="text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>

        <label className="flex items-center gap-2 text-xs text-neutral-600 cursor-pointer select-none">
          <input type="checkbox" checked={spec.deposit_in_total} onChange={(e) => p("deposit_in_total")(e.target.checked)}
            className="h-3.5 w-3.5 rounded accent-blue-600" data-testid="price-deposit-in-total" />
          Include security deposit as a charge in the total
        </label>

        {showAmountPaid && (
          <div className="grid grid-cols-2 gap-2">
            <Field label="Amount already paid" value={spec.amount_paid} onChange={p("amount_paid")} placeholder="0" testId="price-amount-paid" />
            <div>
              <div className="text-[10px] uppercase tracking-wide text-neutral-500 font-semibold">Payment method</div>
              <select value={spec.payment_method} onChange={(e) => p("payment_method")(e.target.value)}
                className="mt-0.5 w-full rounded-lg border border-border px-2 h-8 text-sm" data-testid="price-payment-method">
                {METHODS.map((m) => <option key={m}>{m}</option>)}
              </select>
            </div>
          </div>
        )}

        <Field label="Final total override" calc={calc.grandCalc} overridden={calc.overrides.total}
          value={spec.total_override} onChange={p("total_override")} testId="price-total-override" />
      </div>

      {/* Live summary */}
      <div className="rounded-xl border border-border bg-neutral-50/60 p-3 text-sm space-y-1" data-testid="manual-price-summary">
        <div className="text-xs uppercase tracking-wider text-neutral-500 font-semibold mb-1.5">Booking price summary</div>
        <Row label={`Vehicle rental · €${calc.dailyRate.toFixed(2)} × ${calc.days} day${calc.days === 1 ? "" : "s"}`} v={calc.rental} ov={calc.overrides.rental} testId="sum-rental" />
        {calc.extrasLines.map((e, i) => (
          <Row key={i} subtle testId={`sum-extra-${i}`}
            label={`${e.name}${(e.qty || 1) > 1 ? ` ×${e.qty}` : ""}${e.free ? " (included)" : e.pricing === "per_day" ? ` · €${(parseFloat(e.price) || 0).toFixed(2)}/day` : ""}`}
            v={e.fee} />
        ))}
        {calc.insuranceFee > 0 && <Row label={`Insurance${insuranceName ? ` · ${insuranceName}` : ""}`} v={calc.insuranceFee} ov={calc.overrides.insurance} testId="sum-insurance" />}
        {calc.routeFee > 0 && <Row label="Delivery / one-way fee" v={calc.routeFee} ov={calc.overrides.route} testId="sum-route" />}
        {calc.meetGreet > 0 && <Row label="Meet & Greet" v={calc.meetGreet} />}
        {calc.chargesTotal !== 0 && calc.charges.map((c, i) => <Row key={i} subtle label={c.label} v={parseFloat(c.amount) || 0} />)}
        <div className="border-t border-dashed border-border my-1" />
        <Row label="Subtotal" v={calc.subtotal} testId="sum-subtotal" />
        {calc.discount > 0 && <Row label={`Discount${spec.discount_type === "percent" ? ` (${spec.discount_value}%)` : ""}`} v={-calc.discount} tone="text-emerald-700" testId="sum-discount" />}
        {calc.vat > 0 && <Row label={`VAT ${calc.vatRate}%`} v={calc.vat} testId="sum-vat" />}
        {spec.deposit_in_total && calc.deposit > 0 && <Row label="Security deposit (charged)" v={calc.deposit} />}
        <div className="flex items-center justify-between font-bold text-base pt-1">
          <span>Grand Total{calc.overrides.total && <OverrideBadge />}</span>
          <span className="tabular-nums" data-testid="sum-grand-total"><Money amount={calc.grand} /></span>
        </div>
        {(calc.paid > 0 || paidSoFar > 0) && (
          <>
            <Row label={`Paid${spec.payment_method && calc.paid > 0 ? ` (${spec.payment_method})` : ""}`} v={-(calc.paid || paidSoFar)} tone="text-emerald-700" testId="sum-paid" />
            <div className="flex items-center justify-between font-semibold">
              <span>Balance due</span>
              <span className="tabular-nums" data-testid="sum-balance"><Money amount={Math.max(0, calc.grand - (calc.paid || paidSoFar))} /></span>
            </div>
          </>
        )}
        {!spec.deposit_in_total && calc.deposit > 0 && (
          <Row label="Security deposit (refundable, not in total)" v={calc.deposit} subtle testId="sum-deposit" />
        )}
      </div>
    </div>
  );
}

function Row({ label, v, tone, subtle, ov, testId }) {
  return (
    <div className={`flex items-center justify-between gap-2 ${subtle ? "text-xs text-neutral-500" : ""} ${tone || ""}`} data-testid={testId}>
      <span className="truncate flex items-center">{label}{ov && <OverrideBadge />}</span>
      <span className="tabular-nums shrink-0"><Money amount={v} /></span>
    </div>
  );
}

export default ManualBookingPriceDetails;
